// Copy / native share helpers.
//
// Copy = clipboard only (fast paste). Share = the native OS share sheet via the
// Web Share API (Messages, WhatsApp, AirDrop, Mail…). On desktop / anywhere the
// Web Share API is absent, Share falls back to copy so the button always does
// something useful. All access is guarded — nothing here may throw to the UI.

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export type ShareResult = "shared" | "cancelled" | "copied" | "failed";

export async function shareOrCopy(data: {
  /** v1.0.7 — optional: a message that carries its link inside the text
   *  (the Drop's "tap to claim: <link>") shares and copies the text alone. */
  url?: string;
  text?: string;
  title?: string;
  /** v1.0.16 — what the copy fallback copies, when the url alone would lose the message. */
  copy?: string;
}): Promise<ShareResult> {
  const nav = navigator as Navigator & {
    share?: (d: ShareData) => Promise<void>;
    canShare?: (d: ShareData) => boolean;
  };
  if (typeof nav.share === "function") {
    const payload: ShareData = data.url
      ? { title: data.title, text: data.text, url: data.url }
      : { title: data.title, text: data.text };
    try {
      await nav.share(payload);
      return "shared";
    } catch (e) {
      // User dismissed the sheet (iOS rejects with AbortError). This is NOT a
      // completed share — don't mark it, and don't fall through to copy.
      if ((e as DOMException)?.name === "AbortError") return "cancelled";
      // Any other failure → fall through to the copy fallback.
    }
  }
  return (await copyText(data.copy ?? data.url ?? data.text ?? "")) ? "copied" : "failed";
}

// v1.0.1 — shareNow (open the sheet with no tap, v0.99.14) is gone: after
// Face ID neither iOS browser grants it (27 Sep 2026). A Drop's share is the
// finished slider's tap (sendReceiveFlow.ts, enterShareMode).

/**
 * v1.0.6 — one text for every Drop link, wherever it's shared from (the
 * success card, Balance → Pending, the Drop's own page). No title: some apps
 * turned title + url into "[M® Drop](link)".
 * v1.0.7 — "Here's $0.10 test Drop — tap to claim: <link> — then tell me it's
 * arrived." Chat apps can't hang a link on words, so the link sits right
 * after "tap to claim", inside the text (no separate url, or the sheet would
 * add it a second time at the end).
 */
export function dropSharePayload(amountFig: string, url: string): { text: string } {
  // v1.0.8 — Alexander's wording (28 Sep): the ask, then the link on its own line.
  // v1.0.16 tried text + title + url (like the invite); v1.0.17 — back to two
  // lines, the link inside the text: the order stays as written in every app,
  // and no app turns a title into "[…](link)". The link is short now (a
  // 22-char seed after #, bearer.ts), so it no longer dominates the message.
  return { text: `Here's ${amountFig} test Drop — tell me when you've claimed it.\n${url}` };
}
